/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { ReservationSchedule } from "@lemmonhq/types";
import { ReservationDaySelect } from "./ReservationDaySelect";
import { daysOfWeek as days } from "@lemmonhq/orders";

export interface ServicePeriodsProps {
  schedule: ReservationSchedule;
  startTime: string;
  endTime: string;
  onUpdate: (schedule: ReservationSchedule) => void;
}

export const ServicePeriods = defineComponent<ServicePeriodsProps>({
  name: "ServicePeriods",
  props: ["schedule", "startTime", "endTime", "onUpdate"],
  setup(props) {
    const { t } = useLocalization();
    const hasSchedule = () => {
      return Object.keys(props.schedule).length > 0;
    };

    const clearSchedule = () => {
      props.onUpdate({});
    };

    const setAlwaysActive = () => {
      const allDays: ReservationSchedule = {};
      days.forEach((day) => {
        allDays[day] = [
          {
            start_time: props.startTime || "07:00",
            end_time: props.endTime || "23:59",
          },
        ];
      });
      props.onUpdate(allDays);
    };

    return () => (
      <div>
        <div class="flex items-center justify-between mb-4">
          <p class="text-sm ">{t("settings.service_periods.description")}</p>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={setAlwaysActive}
              class="btn btn-sm btn-primary"
            >
              {t("settings.service_periods.always_active")}
            </button>
            {hasSchedule() && (
              <button
                type="button"
                onClick={clearSchedule}
                class="btn btn-sm btn-ghost"
              >
                {t("settings.service_periods.clear_schedule")}
              </button>
            )}
          </div>
        </div>

        {!hasSchedule() ? (
          <div class="text-center py-8">
            <span class="material-symbols-outlined text-6xl  mb-4">
              calendar_month
            </span>
            <div class="text-lg font-medium mb-2">
              {t("settings.reservations.no_schedule_set")}
            </div>
            <div class="text-base-content/70 mb-4">
              {t("settings.reservations.create_first_schedule")}
            </div>
            <button
              type="button"
              onClick={() =>
                props.onUpdate({
                  monday: [
                    {
                      start_time: props.startTime || "09:00",
                      end_time: props.endTime || "22:00",
                      max_occupancy: 0,
                      pre_payment: 0,
                    },
                  ],
                })
              }
              class="btn btn-primary"
            >
              {t("settings.service_periods.add_schedule")}
            </button>
          </div>
        ) : (
          <div>
            <div class="alert alert-info/20 border-info/30 mb-4">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-info text-xl">
                  schedule
                </span>
                <div>
                  <div class="font-medium text-info">
                    {t("settings.service_periods.scheduled_title")}
                  </div>
                  <div class="text-sm text-info/80">
                    {t("settings.service_periods.scheduled_desc")}
                  </div>
                </div>
              </div>
            </div>

            <ReservationDaySelect
              value={props.schedule}
              onChange={props.onUpdate}
            />
          </div>
        )}
      </div>
    );
  },
});
